import { useEffect, useState } from "preact/hooks";
import { AppShell } from "./components/AppShell";
import { LandingPage } from "./pages/Landing";
import { RadarPage } from "./pages/Radar";
import { SkillsMatchPage } from "./pages/SkillsMatch";
import { MethodologyPage } from "./pages/Methodology";
import { MyResultsPage } from "./pages/MyResults";
import { refreshSession, authEnabled } from "./app/auth";
import { hrefFor, navigate, parseRoute, type ObservatoryRoute } from "./app/router";
import { loadProvider, type StaticEvidenceProvider } from "./data/provider";
import { track } from "./app/analytics";
import { t } from "./i18n";

function routeFromLocation(): ObservatoryRoute {
  return parseRoute();
}

function titleFor(route: ObservatoryRoute): string {
  if (route.name === "radar") return t("meta.radar.title");
  if (route.name === "skills-match") return t("meta.match.title");
  if (route.name === "methodology") return t("meta.method.title");
  if (route.name === "my-results") return t("meta.account.title");
  return t("meta.title");
}

export function App() {
  const [route, setRoute] = useState<ObservatoryRoute>(routeFromLocation);
  const [provider, setProvider] = useState<StaticEvidenceProvider | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const cfg = window.BOOSTSCIENCE_OBSERVATORY;
    loadProvider({ includeDraft: Boolean(cfg?.includeDraft), catalogUrl: cfg?.catalogUrl })
      .then(setProvider)
      .catch(() => setError(true));
    if (authEnabled()) {
      void refreshSession().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    const onNav = () => setRoute(routeFromLocation());
    window.addEventListener("popstate", onNav);
    window.addEventListener("bs-observatory-navigate", onNav);
    const click = (event: MouseEvent) => {
      const target = (event.target as HTMLElement | null)?.closest("a");
      if (!target || target.target === "_blank" || event.metaKey || event.ctrlKey) return;
      const url = new URL(target.href, window.location.origin);
      if (url.origin !== window.location.origin) return;
      const base = window.BOOSTSCIENCE_OBSERVATORY?.basePath ?? "";
      const path = url.pathname;
      const isObs = !base ? true : path === base || path.startsWith(`${base}/`);
      if (!isObs) return;
      if (path.includes("/skills-profile/")) return;
      event.preventDefault();
      navigate(path.replace(base, "") || "/", url.search, false);
    };
    document.getElementById("boostscience-observatory")?.addEventListener("click", click);
    return () => {
      window.removeEventListener("popstate", onNav);
      window.removeEventListener("bs-observatory-navigate", onNav);
      document.getElementById("boostscience-observatory")?.removeEventListener("click", click);
    };
  }, []);

  useEffect(() => {
    track("observatory_view", { route: route.name });
    document.title = titleFor(route);
  }, [route]);

  if (error) {
    return (
      <AppShell>
        <div class="obs-error">
          <p>{t("error.load")}</p>
          <button class="obs-btn obs-btn-primary" type="button" onClick={() => window.location.reload()}>
            {t("error.retry")}
          </button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      {route.name === "landing" && <LandingPage provider={provider} />}
      {route.name === "radar" && (provider ? <RadarPage provider={provider} /> : <p class="obs-empty">{t("error.loading")}</p>)}
      {route.name === "skills-match" &&
        (provider ? <SkillsMatchPage provider={provider} /> : <p class="obs-empty">{t("error.loading")}</p>)}
      {route.name === "methodology" &&
        (provider ? <MethodologyPage provider={provider} /> : <p class="obs-empty">{t("error.loading")}</p>)}
      {route.name === "my-results" && <MyResultsPage provider={provider} />}
      {route.name === "unknown" && (
        <div class="obs-empty">
          <p>{t("error.load")}</p>
          <a href={hrefFor("/")}>{t("nav.home")}</a>
        </div>
      )}
    </AppShell>
  );
}
